import React from 'react';
import { ThemedModal } from 'common/components/cardStyles';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { xratesRecipe } from 'common/state/accounts/recipes';
import { defaultXratesState } from 'common/state/accounts/states';
import { doGetXrates } from 'common/state/accounts/thunkActions';
import {
  customerRecipe,
  customerSummariesRecipe,
} from 'common/state/customers/recipes';
import {
  defaultCustomerState,
  defaultCustomerSummariesState,
} from 'common/state/customers/states';
import {
  doGetCustomer,
  doGetAllCustomerSummaries,
} from 'common/state/customers/thunkActions';
import { providerSummariesRecipe } from 'common/state/provider/recipes';
import { defaultProviderSummariesState } from 'common/state/provider/states';
import { doGetProviderSummaries } from 'common/state/provider/thunkActions';
import { CustomerParams } from 'features/customer/customer';
import { useState, useEffect, useMemo } from 'react';
import FadeIn from 'react-fade-in';
import { useParams } from 'react-router';
import styled from 'styled-components';
import {
  AdjustmentModal,
  AdjustmentModalProps,
} from './components/adjustmentModal';
import { AdjustmentsTable } from './components/adjustmentsTable';
import { CustomerInfoTable } from './components/customerInfoTable';
import { StatementsTable } from './components/statementsTable';
import { Days, getDayOfWeek } from 'common/utils/time';

export interface ProviderParams {
  username: string;
}

export type ModalModes = 'adjustment' | null;

const Container = styled.div`
  flex: 1;
  margin: 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: calc(100vw - 2rem);
`;

export const Provider = () => {
  const { username } = useParams<CustomerParams>();
  const [modalMode, setModalMode] = useState<ModalModes>();
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [initialAdjustmentData, setInitialAdjustmentData] = useState<
    AdjustmentModalProps['initialData'] | null
  >();
  const [providerSummaryEndDate, setProviderSummaryEndDate] = useState(
    getDayOfWeek(new Date(), Days.SUNDAY).toISOString().slice(0, 10)
  );

  const [customerState, customerDispatch] = useProducerWithThunks(
    customerRecipe,
    defaultCustomerState
  );
  const [providerSummariesState, providerSummariesDispatch] =
    useProducerWithThunks(
      providerSummariesRecipe,
      defaultProviderSummariesState
    );
  const [customerSummariesState, customerSummariesDispatch] =
    useProducerWithThunks(
      customerSummariesRecipe,
      defaultCustomerSummariesState
    );
  const [xratesState, xratesDispatch] = useProducerWithThunks(
    xratesRecipe,
    defaultXratesState
  );

  useEffect(() => {
    customerSummariesDispatch(doGetAllCustomerSummaries());
    xratesDispatch(doGetXrates());
  }, [customerSummariesDispatch, providerSummariesDispatch, xratesDispatch]);

  useEffect(() => {
    providerSummariesDispatch(
      doGetProviderSummaries({ end_date: providerSummaryEndDate })
    );
  }, [providerSummariesDispatch, providerSummaryEndDate]);

  useEffect(() => {
    customerDispatch(doGetCustomer(username));
  }, [customerDispatch, username]);

  useEffect(() => {
    if (shouldRefresh) {
      customerDispatch(doGetCustomer(username));
      customerSummariesDispatch(doGetAllCustomerSummaries());
      providerSummariesDispatch(doGetProviderSummaries({}));
      setShouldRefresh(false);
    }
  }, [
    customerDispatch,
    customerSummariesDispatch,
    providerSummariesDispatch,
    xratesDispatch,
    username,
    shouldRefresh,
  ]);

  const closeModal = () => setModalMode(null);

  const providerSummaries = useMemo(
    () =>
      providerSummariesState.summaries?.filter(
        (summary) => summary.username === username
      ),
    [username, providerSummariesState.summaries]
  );

  return (
    <Container>
      <HeaderContainer>
        <h1>
          {username} {customerState.customer && '-'}{' '}
          {customerState?.customer?.first_name}{' '}
          {customerState?.customer?.last_name}
        </h1>
      </HeaderContainer>
      <CustomerInfoTable
        customer={customerState.customer}
        summaries={providerSummaries}
        isLoading={providerSummariesState.pending}
        error={providerSummariesState.error || customerState.error}
      />
      <StatementsTable
        summaries={providerSummaries}
        xrates={xratesState.xrates}
        providerSummaryEndDate={providerSummaryEndDate}
        setProviderSummaryEndDate={setProviderSummaryEndDate}
        setInitialAdjustmentData={setInitialAdjustmentData}
        setModalMode={setModalMode}
        isLoading={customerState.pending || providerSummariesState.pending}
      />
      <AdjustmentsTable
        setModalMode={setModalMode}
        setInitialAdjustmentData={setInitialAdjustmentData}
        customer={customerState.customer}
        summaries={providerSummaries}
        allSummaries={providerSummariesState.summaries}
        xrates={xratesState.xrates}
        isLoading={providerSummariesState.pending}
        error={providerSummariesState.error}
      />
      <ThemedModal open={modalMode != null}>
        {modalMode === 'adjustment' && (
          <FadeIn>
            <AdjustmentModal
              customerSummaries={customerSummariesState.summaries}
              providerSummaries={providerSummariesState.summaries}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
              initialData={initialAdjustmentData}
            />
          </FadeIn>
        )}
      </ThemedModal>
    </Container>
  );
};
